import React, { ComponentProps, useId } from 'react'
import {
  emptyStateSearchGraphic,
  emptyStateFolderGraphic,
  emptyStatePassGraphic,
  emptyStateSecurityGraphic,
  emptyStateIncorrectGraphic,
  emptyStateProcessingGraphic
} from '@workday/canvas-graphics-web'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
// eslint-disable-next-line custom-config/prevent-chakra-import
import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  Collapse,
  Flex,
  forwardRef,
  Stack,
  StackProps,
  Text,
  TextProps,
  useDisclosure
} from '@chakra-ui/react'
import { Msg } from '~/common/localizationUtils'
import { isDevelopment } from '~/components/jsonschema/VndlyJsonSchemaFormBase/actions/utils'
import { cq } from '~/common/containerQuery'

export const EmptyStateTitle = (props: TextProps) => (
  <Text
    textStyle="body"
    {...props}
    sx={cq({
      maxW: '75ch',
      textWrap: 'balance',
      '[data-empty-state-container][data-is-compact] &': { fontSize: 'md' },
      '[data-empty-state-container]:not([data-is-compact]) &': {
        fontSize: {
          '@emptyStateContainer/base': 'md',
          '@emptyStateContainer/lg': 'lg'
        }
      },
      ...props.sx
    })}
  />
)

export const EmptyStateText = (props: TextProps) => (
  <EmptyStateTitle
    {...props}
    sx={cq({
      '[data-empty-state-container][data-is-compact] &': { fontSize: 'sm' },
      '[data-empty-state-container]:not([data-is-compact]) &': {
        fontSize: {
          '@emptyStateContainer/base': 'sm',
          '@emptyStateContainer/lg': 'md'
        }
      },
      ...props.sx
    })}
  />
)
// Canvas graphics reuse hardcoded paint/filter ids. Scope them per mount so two
// copies on one page don't steal each other's fills.
const EmptyStateImage = ({ svgContents, ...props }: { svgContents: string } & BoxProps) => {
  const uid = useId().replaceAll(':', '')
  const html = svgContents.replaceAll(/(id="|url\(#)([^")]+)/g, `$1${uid}-$2`)
  return (
    <Box
      role="img"
      data-empty-image
      aria-hidden
      alt=""
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& svg': { display: 'block', width: 'auto', height: '100%', maxWidth: '100%' }
      }}
      {...props}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

type EmptyStateProps = {
  title?: React.ReactNode
  graphicHeight?: BoxProps['height']
} & Omit<TextProps, 'title'>

export const CaughtUpGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStatePassGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const CaughtUp = ({ title, ...props }: EmptyStateProps) => (
  <>
    <CaughtUpGraphic />
    <EmptyStateTitle textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_caught_up" />}
    </EmptyStateTitle>
  </>
)

CaughtUp.displayName = 'CaughtUp'

export const NothingToShowGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateFolderGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const NothingToShow = ({ title, ...props }: EmptyStateProps) => (
  <>
    <NothingToShowGraphic />
    <EmptyStateTitle textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_nothing_to_show" />}
    </EmptyStateTitle>
  </>
)

NothingToShow.displayName = 'NothingToShow'

export const NoResultsGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateSearchGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const NoResults = ({ title, graphicHeight, ...props }: EmptyStateProps) => (
  <>
    <NoResultsGraphic height={graphicHeight} />
    <EmptyStateTitle textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_no_search_results" />}
    </EmptyStateTitle>
  </>
)

NoResults.displayName = 'NoResults'

export const LockedOutGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateSecurityGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const LockedOut = ({ title, ...props }: EmptyStateProps) => (
  <>
    <LockedOutGraphic />
    <EmptyStateTitle textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_locked_out" />}
    </EmptyStateTitle>
  </>
)

LockedOut.displayName = 'LockedOut'

export const RefreshErrorGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateProcessingGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const RefreshError = ({
  title,
  error,
  onRefresh,
  graphicHeight,
  ...props
}: EmptyStateProps & {
  error?: string | Error | null // Allow string or Error object
  onRefresh?: () => void // Optional refresh handler
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: Boolean(isDevelopment() && error) })
  return (
    <>
      <RefreshErrorGraphic height={graphicHeight} />
      <EmptyStateTitle textStyle="bodySemi" {...props}>
        {title ?? <Msg id="common.ui.error_processing_request_try_again" />}
      </EmptyStateTitle>
      <ButtonGroup justifyContent="center">
        {onRefresh && (
          <Button variant="link" onClick={onRefresh}>
            <Msg id="common.ui.refresh" />
          </Button>
        )}
        {isDevelopment() && error && (
          <Button variant="link" onClick={onToggle}>
            <Msg id="term.details" />
          </Button>
        )}
      </ButtonGroup>
      <Collapse in={isOpen}>
        <Stack as="pre" whiteSpace="pre-wrap" w="full" bg="gray.100" p={3} fontSize="sm" mt={4} textAlign="left">
          {typeof error === 'string' ? (
            error
          ) : (
            <>
              <Box>{error?.message}</Box>
              <Box>{error?.stack}</Box>
            </>
          )}
        </Stack>
      </Collapse>
    </>
  )
}

RefreshError.displayName = 'RefreshError'

export const UnexpectedErrorGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateIncorrectGraphic.svg} />
)
/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const UnexpectedError = ({ title, ...props }: EmptyStateProps) => (
  <>
    <UnexpectedErrorGraphic />
    <EmptyStateTitle textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_browser_error" />}
    </EmptyStateTitle>
  </>
)

UnexpectedError.displayName = 'UnexpectedError'

/**
 * A component that provides a consistent layout for empty states across the application.
 *
 * You'll need to nest the specific empty state graphic and text inside this component.
 *
 * @example
 * ```
 * <EmptyState>
 *   <EmptyState.CaughtUp title="An Overridden Title" />
 *   <Text>Optional Description</Text>
 *   <Link variant="link" href="#">
 *     Optional Link
 *   </Link>
 * </EmptyState>
 * ```
 * @see: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const EmptyState = Object.assign(
  forwardRef(({ isCompact, children, ...props }: StackProps & { isCompact?: boolean }, ref) => {
    return (
      <Box
        data-empty-state-container
        data-is-compact={isCompact ? '' : undefined}
        sx={{
          w: 'full',
          containerType: 'inline-size',
          containerName: 'emptyStateContainer',
          // 0 matches containerQuery's "base" breakpoint
          '@container emptyStateContainer (min-width: 0px)': {
            '[data-container]': { p: 6 },
            '[data-empty-image]': { display: 'none' }
          },
          // 512 matches containerQuery's "lg" breakpoint
          '@container emptyStateContainer (min-width: 512px)': {
            '[data-container]': isCompact ? {} : { p: 12, '&>*~*': { mt: '8px' } },
            '[data-empty-image]': { display: isCompact ? 'none' : 'block', width: '360px', height: '180px' }
          },
          // 1024 matches containerQuery's "5xl" breakpoint
          '@container emptyStateContainer (min-width: 1024px)': {
            '[data-empty-image]': { width: '480px', height: '240px' }
          }
        }}>
        <Flex
          data-container
          textAlign="center"
          flexDirection="column"
          alignItems="center"
          role="alert"
          ref={ref}
          {...props}>
          {children}
        </Flex>
      </Box>
    )
  }),
  {
    CaughtUp,
    NothingToShow,
    NoResults,
    LockedOut,
    RefreshError,
    UnexpectedError,
    Text: EmptyStateText
  }
)

EmptyState.displayName = 'EmptyState'

/**
 * Wraps a region of a page in an ErrorBoundary that will be shown when there is an unexpected error.
 */
export function RegionErrorBoundary({
  onError,
  onReset,
  resetKeys,
  isCompact,
  children
}: Omit<ComponentProps<typeof ReactErrorBoundary>, 'FallbackComponent' | 'fallback' | 'fallbackRender'> & {
  isCompact?: boolean
}) {
  return (
    <ReactErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <RegionError error={error} onRefresh={resetErrorBoundary} isCompact={isCompact} />
      )}
      onError={onError}
      onReset={onReset}
      resetKeys={resetKeys}>
      {children}
    </ReactErrorBoundary>
  )
}

/**
 * An error meant to be shown in place of a region of the page when there is an unexpected error.
 * It's likely easier for you to just use `RegionErrorBoundary` instead of this component directly.
 *
 * @param error
 * @param onRefresh The function that will be called when the user clicks the refresh button.
 */
export function RegionError({
  error,
  onRefresh,
  isCompact
}: {
  error?: any
  onRefresh?: () => void
  isCompact?: boolean
}) {
  const { isOpen, onToggle } = useDisclosure()
  return (
    <EmptyState isCompact={isCompact}>
      <EmptyState.UnexpectedError />
      <EmptyState.Text mt={2} onClick={onToggle}>
        <Msg id="common.generic.error.issues_displaying_content" />
      </EmptyState.Text>
      {!isCompact ? (
        <>
          <ButtonGroup justifyContent="center">
            {onRefresh && (
              <Button variant="link" onClick={onRefresh}>
                <Msg id="common.ui.refresh" />
              </Button>
            )}
            {isDevelopment() && error && (
              <Button variant="link" onClick={onToggle}>
                Stack Trace
              </Button>
            )}
          </ButtonGroup>
          {error ? (
            <Collapse in={isOpen}>
              <Box as="pre" whiteSpace="pre-wrap" w="full" bg="gray.100" p={3} fontSize="sm" mt={4} textAlign="left">
                {error?.stack ?? error?.message ?? (typeof error === 'string' ? error : null)}
              </Box>
            </Collapse>
          ) : null}
        </>
      ) : null}
    </EmptyState>
  )
}
